import type { SubscriptionPromptModel } from './index';
import { isSubscriptionUser } from './isSubscriptionUser';
import { log } from './log';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { DefaultError } from '@tanstack/query-core';
import { SecurityPackage } from '@thinkalpha/access-ws-client/contracts/user.js';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getPlanChangeSubscription, isActiveSubscriptionStatus } from 'src/models/SubscriptionPlanCatalogModel/rules';
import type { SubscriptionSetupModel } from 'src/models/SubscriptionSetupModel';
import { getUserSecurityPackageQuery } from 'src/queries/user';
import type { ReactBindings } from 'src/types/bindings';
import type { z } from 'zod';

@injectable()
export class SubscriptionPromptModelImpl implements SubscriptionPromptModel {
    readonly #disposableStack = new DisposableStack();

    #open = signal(false);
    #billingOpen = signal(false);
    #billingPlanChangeSubscriptionId = signal<string | null>(null);
    #billingPlanChangeImmediatePreferred = signal(false);
    #autoShown = false;

    #securityPackageQuery: QueryObserverModel<
        z.infer<typeof SecurityPackage>,
        DefaultError,
        z.infer<typeof SecurityPackage>
    >;

    readonly loading: ReadonlySignal<boolean> = computed(
        () => this.#securityPackageQuery?.signal.value.isFetching ?? true,
    );

    // True when the user's access model is subscription-based (regardless of whether they currently have an active subscription)
    readonly isSubscriptionUser: ReadonlySignal<boolean> = computed(() => {
        const secPkg = this.#securityPackageQuery?.signal.value?.data;
        if (TRACE) {
            log.trace('isSubscriptionUser evaluating:', {
                secPkgStatus: this.#securityPackageQuery?.signal.value.status,
                secPkgError: this.#securityPackageQuery?.signal.value.error?.message,
                secPkgData: secPkg
                    ? {
                          roles: secPkg.roles.length,
                          accessList: secPkg.accessList.map((a) => a.type),
                          subscriptions: secPkg.subscriptions.length,
                      }
                    : undefined,
            });
        }

        if (!secPkg) {
            if (TRACE) {
                log.trace('isSubscriptionUser evaluating: no security package data yet');
            }
            return false;
        }
        const result = isSubscriptionUser(secPkg.accessList);
        if (TRACE) {
            log.trace('isSubscriptionUser evaluating: result:', {
                result,
            });
        }
        return result;
    });

    readonly needsSubscription: ReadonlySignal<boolean> = computed(() => {
        const secPkgResult = this.#securityPackageQuery?.signal.value;
        const secPkg = secPkgResult?.data;

        if (TRACE) {
            log.trace('needsSubscription evaluating:', {
                secPkgStatus: secPkgResult?.status,
                secPkgError: secPkgResult?.error?.message,
                secPkgData: secPkg
                    ? {
                          roles: secPkg.roles.length,
                          accessList: secPkg.accessList.map((a) => a.type),
                          subscriptions: secPkg.subscriptions.length,
                      }
                    : undefined,
            });
        }

        if (!secPkg) return false;

        const hasActiveSubscription = secPkg.subscriptions.some((s) => isActiveSubscriptionStatus(s.status));
        const hasRoles = secPkg.roles.length > 0;

        if (TRACE) {
            log.trace('needsSubscription result:', {
                isSubscriptionUser: this.isSubscriptionUser.value,
                hasActiveSubscription,
                hasRoles,
                result: this.isSubscriptionUser.value && !hasActiveSubscription && !hasRoles,
            });
        }

        return this.isSubscriptionUser.value && !hasActiveSubscription && !hasRoles;
    });

    readonly open: ReadonlySignal<boolean> = this.#open;
    readonly billingOpen: ReadonlySignal<boolean> = this.#billingOpen;
    readonly isAnyModalOpen: ReadonlySignal<boolean> = computed(() => this.#open.value || this.#billingOpen.value);
    readonly billingPlanChangeSubscriptionId: ReadonlySignal<string | null> = this.#billingPlanChangeSubscriptionId;
    readonly billingPlanChangeImmediatePreferred: ReadonlySignal<boolean> = this.#billingPlanChangeImmediatePreferred;

    constructor(
        @inject('QueryObserverModelFactory')
        private queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('SubscriptionSetupModel') private subscriptionSetupModel: SubscriptionSetupModel,
    ) {
        this.#securityPackageQuery = this.queryObserverModelFactory<
            z.infer<typeof SecurityPackage>,
            DefaultError,
            z.infer<typeof SecurityPackage>
        >(getUserSecurityPackageQuery('me'));
        this.#disposableStack.use(this.#securityPackageQuery);

        // Auto-open the modal when needsSubscription becomes true (first time only)
        this.#disposableStack.defer(
            effect(() => {
                const needs = this.needsSubscription.value;
                if (TRACE) {
                    log.trace('auto-open effect:', {
                        needsSubscription: needs,
                        autoShown: this.#autoShown,
                    });
                }
                if (needs && !this.#autoShown) {
                    this.#autoShown = true;
                    this.subscriptionSetupModel.reset();
                    this.#billingPlanChangeSubscriptionId.value = null;
                    this.#billingPlanChangeImmediatePreferred.value = false;
                    this.#open.value = true;
                    if (TRACE) {
                        log.trace('auto-opening modal');
                    }
                }
            }),
        );
    }

    openSubscriptionSetup(): void {
        if (TRACE) {
            log.trace('openSubscriptionSetup called');
        }
        this.subscriptionSetupModel.reset();
        this.#billingPlanChangeSubscriptionId.value = null;
        this.#billingPlanChangeImmediatePreferred.value = false;
        this.#open.value = true;
    }

    closeSubscriptionSetup(): void {
        if (TRACE) {
            log.trace('closeSubscriptionSetup called');
        }
        this.#open.value = false;
    }

    openPlansOrBilling(): void {
        if (TRACE) {
            log.trace('openPlansOrBilling called', {
                needsSubscription: this.needsSubscription.value,
            });
        }

        if (this.needsSubscription.value) {
            this.openSubscriptionSetup();
            return;
        }

        const subscriptions = this.#securityPackageQuery.signal.value.data?.subscriptions ?? [];
        const planChangeSubscription = getPlanChangeSubscription(subscriptions);
        this.#billingPlanChangeSubscriptionId.value = planChangeSubscription?.id ?? null;
        this.#billingPlanChangeImmediatePreferred.value = planChangeSubscription !== undefined;
        this.#billingOpen.value = true;
    }

    openBilling(): void {
        if (TRACE) {
            log.trace('openBilling called');
        }
        this.#billingPlanChangeSubscriptionId.value = null;
        this.#billingPlanChangeImmediatePreferred.value = false;
        this.#billingOpen.value = true;
    }

    closeBilling(): void {
        if (TRACE) {
            log.trace('closeBilling called');
        }
        this.#billingPlanChangeSubscriptionId.value = null;
        this.#billingPlanChangeImmediatePreferred.value = false;
        this.#billingOpen.value = false;
    }

    consumeBillingPlanChangeRequest(): void {
        this.#billingPlanChangeSubscriptionId.value = null;
        this.#billingPlanChangeImmediatePreferred.value = false;
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
